import React from 'react';
import { Category, EventPageType } from '@graphql/generated-base';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation as useTranslationCore } from '@shared/core';
import { StyledRoot, styles } from './EventLogisticBlock.styles';
import { ViewDetailsArrow } from '@apps/guest/components/common/ViewDetailsArrow/ViewDetailsArrow';
import { useShouldShowViewDetailsArrow } from '@apps/guest/components/common/ViewDetailsArrow/useShouldShowViewDetailsArrow';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  display1ToLocationOverride,
  display2ToDateOverride,
  hed4ToLocationOverride,
  hed5ToDateOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { useFormattedDate } from '@localization/hooks/useFormattedDate';
import { Skeleton } from '@shared/components/Skeleton';
import { useEventLocation } from '@shared/utils/eventLocation';
import { EventCountdown } from '@apps/guest/components/common/EventCountdown/EventCountdown';
import type { EventLogisticsBlockProps } from './EventLogisticBlock.types';
import { useScaleGuestSiteFontSize } from '@shared/core/website/theming/useScaleGuestSiteFontSize';
import { pxToRem } from '@withjoy/joykit/theme';

const useEventDateHeader = (date: EventLogisticsBlockProps['date'], shouldBreak: boolean) => {
  return (
    useFormattedDate(date?.timestamp, shouldBreak ? 'weekday\ndd MMMM yyyy' : 'weekday, dd MMMM yyyy', {
      titlecase: true,
      timezone: date?.timezone,
      isoTimestamp: date?.timestamp,
    }) ?? null
  );
};

const typographyOverrides = {
  eventDate: [...hed5ToDateOverride, ...display2ToDateOverride],
  eventLocation: [...display1ToLocationOverride, ...hed4ToLocationOverride],
};

const EventLogisticsBlock: React.FC<EventLogisticsBlockProps> = ({
  children,
  page,
  eventHandle,
  location,
  date,
  shouldBreakDate = false,
  hideEventCountdown,
  showViewDetailsButton,
  onViewDetailsButtonClicked,
  pages,
  ...restProps
}) => {
  const inlineEditorTransFn = useTranslationCore('joykit').t('inlineEditor');

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const eventDateHeader = useEventDateHeader(date, shouldBreakDate);
  const queryString = useQueryString();
  const { shouldShow: shouldShowViewDetailsArrow, nextPageSlug } = useShouldShowViewDetailsArrow(pages);

  const { eventLocationDisplayName, isLoadingEventLocation } = useEventLocation({ locationName: location });

  const handleEditEventDateClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editDate',
        inlineEditData: { date: date?.dateString || '', timeZone: date?.timezone || 'America/Los_Angeles', label: inlineEditorTransFn.eventDate() },
      },
    });
  });

  const handleEditEventLocationClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editLocation', inlineEditData: { location: location || '', label: inlineEditorTransFn.eventLocation() } },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  if (!children && !location && !date) {
    return null;
  }

  return (
    // position relative should prevent the back sprite from being above the z of this box
    <StyledRoot
      __css={styles.root}
      {...restProps}
    >
      <Flex
        flexDirection={'column'}
        textAlign={'center'}
      >
        {eventDateHeader && (
          <InlineEditor
            elementLabel={inlineEditorTransFn.eventDate()}
            actionData={{
              edit: handleEditEventDateClick,
              font: () => handleEditFontClick(Category.DATE),
            }}
            componentName="eventDate"
            pageName={EventPageType.welcome}
            pageSlug="welcome"
            wrapperCSS={{ zIndex: 100 }}
          >
            <GuestSiteTypographyOverride override={typographyOverrides.eventDate}>
              <TextV2
                data-testid={'logistics-date'}
                typographyVariant={{ _: 'hed5', sm2: 'display2' }}
                fontSize={{ _: scaleFontSize(pxToRem(22), Category.DATE), sm2: scaleFontSize(pxToRem(32), Category.DATE) }}
                whiteSpace={'pre-wrap'}
                fontWeight={{ _: 400, sm2: 400 }}
              >
                {eventDateHeader}
              </TextV2>
            </GuestSiteTypographyOverride>
          </InlineEditor>
        )}
        {location && (
          <Box marginTop={{ _: 3, sm2: 5 }}>
            <InlineEditor
              elementLabel={inlineEditorTransFn.eventLocation()}
              actionData={{
                edit: handleEditEventLocationClick,
                font: () => handleEditFontClick(Category.LOCATION),
              }}
              componentName="eventLocation"
              pageName={EventPageType.welcome}
              pageSlug="welcome"
              wrapperCSS={{ zIndex: 100 }}
            >
              <GuestSiteTypographyOverride override={typographyOverrides.eventLocation}>
                {isLoadingEventLocation ? (
                  <Skeleton
                    data-testid={'logistics-location'}
                    placeholder={<Box typographyVariant={{ _: 'hed4', sm2: 'display1' }}>&nbsp;</Box>}
                  />
                ) : (
                  <TextV2
                    data-testid={'logistics-location'}
                    typographyVariant={{ _: 'hed4', sm2: 'display1' }}
                    textTransform="none"
                    fontWeight={{ _: 400, sm2: 400 }}
                  >
                    {eventLocationDisplayName}
                  </TextV2>
                )}
              </GuestSiteTypographyOverride>
            </InlineEditor>
          </Box>
        )}
        {!hideEventCountdown && date && (
          <EventCountdown
            data-testid={'logistics-countdown'}
            milliseconds={date.milliseconds}
            timezone={date.timezone}
            marginTop={7}
          />
        )}
      </Flex>
      {children && <>{children}</>}
      {/* View Details + Arrow */}
      {shouldShowViewDetailsArrow && nextPageSlug && eventHandle ? (
        <ViewDetailsArrow
          show={!!showViewDetailsButton}
          eventHandle={eventHandle}
          nextPageSlug={nextPageSlug}
          queryString={queryString}
          onViewDetailsButtonClicked={onViewDetailsButtonClicked}
        />
      ) : null}
    </StyledRoot>
  );
};

EventLogisticsBlock.displayName = 'EventLogisticsBlock';

export { EventLogisticsBlock };
